import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { t } from "@/lib/i18n";
import "@/lib/i18n_p1";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

// Flip to true once Stripe live keys are configured in backend/.env.
// While false, paid tier CTAs show "Coming soon" and no checkout is attempted.
const PAID_TIERS_ENABLED = false;

function PublicShell({ children, lang }) {
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="border-b border-[#E9ECEF] bg-white">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-8 h-16">
          <Link to="/" className="flex items-center gap-3">
            <span className="inline-block w-2 h-6 bg-[#C65D47]" />
            <span className="font-display text-xl">Law Pilot</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link to="/login" className="text-sm px-3 py-2 hover:bg-[#F1F3F5]">{t(lang,"nav.login")}</Link>
            <Link to="/register" className="bg-[#0A1128] text-white text-sm px-4 py-2 hover:bg-[#1C2538]">{t(lang,"nav.start")}</Link>
          </div>
        </div>
      </header>
      <main className="max-w-7xl w-full mx-auto px-4 md:px-8 py-12">{children}</main>
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(null);
  const [areebaBusy, setAreebaBusy] = useState(null);
  const [areebaConfigured, setAreebaConfigured] = useState(false);
  const [hasPortal, setHasPortal] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/tiers").then(({ data }) => setTiers(data));
    api.get("/areeba/status").then(({ data }) => setAreebaConfigured(!!data.configured)).catch(() => {});
    if (user) {
      api.get("/payments/subscription").then(({ data }) => {
        setSub(data.subscription);
        setHasPortal(!!data.has_customer_portal);
      });
    }
  }, [user]);

  const subscribe = async (tier_id) => {
    if (!user) { navigate("/login"); return; }
    setBusy(tier_id);
    try {
      const { data } = await api.post("/payments/checkout", {
        tier_id,
        origin_url: window.location.origin,
      });
      window.location.assign(data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/payments/portal", {
        return_url: window.location.origin + "/pricing",
      });
      window.location.assign(data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
      setPortalBusy(false);
    }
  };

  const subscribeAreeba = async (tier_id) => {
    if (!user) { navigate("/login"); return; }
    setAreebaBusy(tier_id);
    try {
      const { data } = await api.post("/areeba/checkout", {
        tier_id,
        origin_url: window.location.origin,
      });
      window.location.assign(data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
      setAreebaBusy(null);
    }
  };

  const current = sub?.tier_id;
  const content = (
    <>
      <div className="editorial-rule pt-4">
        <p className="text-xs tracking-[0.24em] uppercase text-[#C65D47]">{t(lang,"pricing.kicker")}</p>
        <div className="flex items-end justify-between flex-wrap gap-4">
          <h1 className="font-display text-4xl md:text-5xl mt-2" data-testid="pricing-title">{t(lang,"pricing.title")}</h1>
          {hasPortal && (
            <button
              onClick={openPortal}
              disabled={portalBusy}
              data-testid="manage-subscription-btn"
              className="inline-flex items-center gap-2 border border-[#0A1128] text-[#0A1128] px-4 py-2 text-sm hover:bg-white disabled:opacity-60"
            >
              {portalBusy ? t(lang,"common.loading") : t(lang,"pricing.manage")}
            </button>
          )}
        </div>
        <p className="mt-3 text-[#495057] max-w-2xl">{t(lang,"pricing.sub")}</p>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E9ECEF] border border-[#E9ECEF]">
        {tiers.map((tier, idx) => {
          const isMost = tier.id === "pro";
          const isCurrent = current === tier.id;
          return (
            <div key={tier.id} className={`bg-white p-8 relative ${isMost ? "md:-mt-2 md:-mb-2 md:border-y-2 md:border-y-[#C65D47]" : ""}`} data-testid={`pricing-card-${tier.id}`}>
              {isMost && <span className="absolute top-0 end-8 -translate-y-1/2 bg-[#C65D47] text-white text-xs tracking-[0.2em] uppercase px-3 py-1">{t(lang,"pricing.best")}</span>}
              <div className="flex items-baseline gap-2">
                <h3 className="font-display text-3xl">{tier.name}</h3>
                {tier.id === "firm" && <Sparkles className="w-4 h-4 text-[#C65D47]" />}
              </div>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="font-display text-5xl">${tier.amount}</span>
                <span className="text-[#868E96]">{t(lang,"pricing.mo")}</span>
              </div>
              <ul className="mt-8 space-y-3">
                {tier.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#0A1128]">
                    <Check className="w-4 h-4 mt-1 text-[#C65D47] shrink-0" /> <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                disabled={busy === tier.id || isCurrent || !PAID_TIERS_ENABLED}
                onClick={() => subscribe(tier.id)}
                data-testid={`subscribe-${tier.id}-btn`}
                className={`mt-8 w-full inline-flex items-center justify-center gap-2 px-4 py-3 transition-colors ${
                  isCurrent ? "bg-[#F1F3F5] text-[#495057] cursor-default"
                  : !PAID_TIERS_ENABLED ? "bg-[#F1F3F5] text-[#495057] cursor-not-allowed"
                  : isMost ? "bg-[#C65D47] text-white hover:bg-[#A94834]"
                  : "bg-[#0A1128] text-white hover:bg-[#1C2538]"
                } disabled:opacity-60`}
              >
                {isCurrent ? t(lang,"pricing.current")
                  : !PAID_TIERS_ENABLED ? t(lang,"pricing.coming_soon")
                  : busy === tier.id ? t(lang,"common.loading")
                  : <>{t(lang,"pricing.cta")} <ArrowRight className="w-4 h-4" /></>}
              </button>
              {areebaConfigured && !isCurrent && (
                <button
                  disabled={areebaBusy === tier.id}
                  onClick={() => subscribeAreeba(tier.id)}
                  data-testid={`subscribe-areeba-${tier.id}-btn`}
                  className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-[#0A1128] text-[#0A1128] hover:bg-[#F8F9FA] transition-colors disabled:opacity-60 text-sm"
                >
                  {areebaBusy === tier.id ? t(lang,"common.loading") : <>Pay with Areeba (LB) <ArrowRight className="w-4 h-4" /></>}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {!user && (
        <p className="mt-6 text-sm text-[#495057]">
          {t(lang,"pricing.free_label")} — <Link to="/register" className="underline">{t(lang,"nav.start")}</Link>
        </p>
      )}
    </>
  );

  return user ? <AppShell>{content}</AppShell> : <PublicShell lang={lang}>{content}</PublicShell>;
}
